import { useMemo } from 'react'

import {
  ConversationCountQuery,
  useCubeReportAccessFiltersContext,
  useCubeReportPageFiltersContext,
} from 'cube'
import { CONVERSATION_FACTS_DIMENSIONS } from 'cube/constants/QueryConstants'

import { CubeQueryBuilder, useCubeQuery } from '@driftt/dds.reports'

import { resolveCubeKPIResults } from 'components/KPICard'
import useTimePeriod from 'hooks/useTimePeriod'

export interface MedianConversationLengthCountResult {
  resultCount?: number | null
}

/***
 * Hook that uses cube to fetch conversation length data for the current and previous time period.
 * Median conversation length is defined as the median duration between the first and last enduser interaction in seconds.
 *
 * @returns {MedianConversationLengthCountResult}
 */
export const useConversationCountForMedian =
  (): MedianConversationLengthCountResult => {
    const timePeriod = useTimePeriod()
    const { pageFilters } = useCubeReportPageFiltersContext()
    const { accessFilters } = useCubeReportAccessFiltersContext()

    const conversationCountForMedianQuery = new CubeQueryBuilder({
      query: ConversationCountQuery,
    })
      .withName('ConversationCountForMedian')
      .withFilters([...pageFilters, ...accessFilters])
      .withinDateRange(
        CONVERSATION_FACTS_DIMENSIONS.CONVERSATION_CREATED_AT,
        timePeriod.startDate.toDate(),
        timePeriod.endDate.toDate()
      )
      .build()

    const { data } = useCubeQuery({
      query: conversationCountForMedianQuery,
    })

    const conversationCountForMedian = useMemo(
      () => resolveCubeKPIResults(data, ConversationCountQuery.queryName ?? ''),
      [data]
    )

    const resultCount = useMemo(
      () => conversationCountForMedian ?? null,
      [conversationCountForMedian]
    )

    return useMemo(() => ({ resultCount }), [resultCount])
  }

export default useConversationCountForMedian
